@let list = state.paged();
@let active = state.activeItem();

@if (showGallery) {
  <!-- Toolbar -->
  @if (showToolbar) {
    <div class="mv-toolbar" aria-label="Media viewer toolbar">
      <div class="mv-toolbar__group mv-toolbar__group--grow">
        @if (showSearch) {
          <label class="mv-search">
            <app-icon name="search" [size]="16" />
            <input
              class="mv-search__input"
              type="search"
              placeholder="Search by name or extension…"
              [value]="state.search()"
              (input)="onSearch($any($event.target).value)"
              aria-label="Search media"
            />
          </label>
        }
      </div>

      <div class="mv-toolbar__group">
        @if (showFilter) {
          <address class="mv-segment" aria-label="Filter by type">
            @for (option of typeOptions; track option.value) {
              <button
                type="button"
                class="mv-chip"
                [class.mv-chip--active]="state.typeFilter() === option.value"
                (click)="onTypeFilter(option.value)"
                [attr.aria-pressed]="state.typeFilter() === option.value"
              >
                {{ option.label }}
              </button>
            }
          </address>
        }

        @if (showSort) {
          <address class="mv-segment" aria-label="Sort by">
            @for (option of sortOptions; track option.value) {
              <button
                type="button"
                class="mv-chip"
                [class.mv-chip--active]="state.sort().field === option.value"
                (click)="onSortField(option.value)"
                [ngbTooltip]="'Sort by ' + option.label"
              >
                {{ option.label }}
                @if (state.sort().field === option.value) {
                  <app-icon
                    [name]="state.sort().dir === 'asc' ? 'chevronUp' : 'chevronDown'"
                    [size]="13"
                  />
                }
              </button>
            }
          </address>
        }

        @if (showRefresh) {
          <button
            type="button"
            class="mv-tool"
            (click)="onRefresh()"
            ngbTooltip="Refresh"
            aria-label="Refresh"
          >
            <app-icon name="refresh" [size]="18" />
          </button>
        }
        @if (showViewToggle) {
          <button
            type="button"
            class="mv-tool"
            (click)="toggleView()"
            [ngbTooltip]="state.view() === 'grid' ? 'List view' : 'Grid view'"
            [attr.aria-label]="
              state.view() === 'grid' ? 'Switch to list view' : 'Switch to grid view'
            "
          >
            <app-icon [name]="state.view() === 'grid' ? 'list' : 'grid'" [size]="18" />
          </button>
        }
        @if (showUpload) {
          <button
            type="button"
            class="mv-tool mv-tool--accent"
            (click)="onUpload()"
            ngbTooltip="Upload"
            aria-label="Upload media"
          >
            <app-icon name="upload" [size]="18" />
          </button>
        }
      </div>
    </div>
  }

  <!-- Extension facet -->
  @if (showFilter && state.availableExtensions().length > 1) {
    <address class="mv-facets" aria-label="Filter by extension">
      @for (ext of state.availableExtensions(); track trackByExt($index, ext)) {
        <button
          type="button"
          class="mv-facet"
          [class.mv-facet--active]="state.extensions().includes(ext)"
          (click)="toggleExtension(ext)"
          [attr.aria-pressed]="state.extensions().includes(ext)"
        >
          .{{ ext }}
        </button>
      }
    </address>
  }

  <!-- Gallery -->
  <div class="mv-body">
    @if (loading) {
      <div class="mv-grid" aria-hidden="true">
        @for (skeleton of skeletons; track $index) {
          <div class="mv-card mv-card--skeleton">
            <app-skeleton shape="rectangle" width="100%" height="150px" animation="wave" />
            <div class="mv-card__meta">
              <app-skeleton shape="text" width="80%" />
              <app-skeleton shape="text" width="40%" />
            </div>
          </div>
        }
      </div>
    } @else if (list.length === 0) {
      <output class="mv-empty">
        <span class="mv-empty__icon"><app-icon name="image" [size]="40" /></span>
        <p class="mv-empty__text">{{ emptyText }}</p>
      </output>
    } @else {
      <div [class]="state.view() === 'grid' ? 'mv-grid' : 'mv-list'">
        @for (item of list; track trackById($index, item)) {
          <button
            #card
            type="button"
            class="mv-card"
            [class.mv-card--list]="state.view() === 'list'"
            [attr.data-video-id]="item.type === 'video' && !item.thumbnailUrl ? item.id : null"
            [attr.data-video-url]="item.type === 'video' && !item.thumbnailUrl ? item.url : null"
            [attr.aria-label]="'Open ' + item.name"
            (click)="onCardActivate(item)"
          >
            <div class="mv-card__thumb">
              @if (thumbFor(item); as thumb) {
                <img
                  class="mv-card__img"
                  [src]="thumb"
                  [alt]="item.name"
                  loading="lazy"
                  decoding="async"
                />
              } @else {
                <span class="mv-card__fallback">
                  <app-icon [name]="item.type === 'video' ? 'film' : 'image'" [size]="34" />
                </span>
              }

              @if (item.type === 'video') {
                <span class="mv-card__play" aria-hidden="true"
                  ><app-icon name="play" [size]="20"
                /></span>
                @if (formatDuration(item.duration); as dur) {
                  <span class="mv-card__badge mv-card__badge--time">{{ dur }}</span>
                }
              }
              <span class="mv-card__badge mv-card__badge--ext">.{{ item.extension }}</span>

              @if (showFavorite) {
                <button
                  type="button"
                  class="mv-card__fav"
                  [class.mv-card__fav--on]="item.favorite"
                  (click)="onFavorite($event, item)"
                  [attr.aria-pressed]="item.favorite"
                  [attr.aria-label]="
                    item.favorite ? 'Unfavourite ' + item.name : 'Favourite ' + item.name
                  "
                >
                  <app-icon name="star" [size]="16" />
                </button>
              }
              @if (showDelete) {
                <button
                  type="button"
                  class="mv-card__delete"
                  (click)="onDelete($event, item)"
                  [attr.aria-label]="'Delete ' + item.name"
                >
                  <app-icon name="trash" [size]="16" />
                </button>
              }
            </div>

            <div class="mv-card__meta">
              <span class="mv-card__name" [ngbTooltip]="item.name">{{ item.name }}</span>
              <span class="mv-card__sub">
                {{ formatSize(item.size) }} · {{ item.createdDate | date: 'mediumDate' }}
              </span>
            </div>
          </button>
        }
      </div>
    }
  </div>

  <!-- Pager -->
  @if (!loading && state.pageCount() > 1) {
    <nav class="mv-pager" aria-label="Gallery pages">
      <button
        type="button"
        class="mv-tool"
        (click)="goToPage(state.page() - 1)"
        [disabled]="state.page() <= 1"
        aria-label="Previous page"
      >
        <app-icon name="chevronLeft" [size]="18" />
      </button>
      <span class="mv-pager__label">
        Page {{ state.page() }} / {{ state.pageCount() }} · {{ state.filteredCount() }} item(s)
      </span>
      <button
        type="button"
        class="mv-tool"
        (click)="goToPage(state.page() + 1)"
        [disabled]="state.page() >= state.pageCount()"
        aria-label="Next page"
      >
        <app-icon name="chevronRight" [size]="18" />
      </button>
    </nav>
  }
}

<!-- Lightbox -->
@if (state.lightboxOpen() && active) {
  <dialog
    #lightbox
    class="mv-lightbox"
    open
    tabindex="-1"
    aria-modal="true"
    [attr.aria-label]="active.name"
    (pointerdown)="onPointerDown($event)"
    (pointermove)="onPointerMove($event)"
    (pointerup)="onPointerUp($event)"
    (pointercancel)="onPointerUp($event)"
    (wheel)="onWheel($event)"
  >
    <!-- Lightbox toolbar -->
    <div class="mv-lightbox__bar">
      <span class="mv-lightbox__title" [ngbTooltip]="active.name">
        <app-icon [name]="active.type === 'video' ? 'film' : 'image'" [size]="16" />
        <span class="mv-lightbox__name">{{ active.name }}</span>
      </span>

      <div class="mv-lightbox__tools">
        @for (tool of lightboxTools; track tool.id) {
          @if (tool.visible()) {
            @switch (tool.kind) {
              @case ('divider') {
                <span class="mv-lightbox__divider"></span>
              }
              @case ('zoom') {
                <span class="mv-lightbox__zoom" aria-live="polite">{{ state.zoom() }}%</span>
              }
              @default {
                <button
                  type="button"
                  class="mv-tool"
                  (click)="tool.action!()"
                  [ngbTooltip]="tool.title!()"
                  [attr.aria-label]="tool.ariaLabel!()"
                >
                  <app-icon [name]="tool.icon!()" [size]="18" />
                </button>
              }
            }
          }
        }
      </div>
    </div>

    <!-- Prev / Next -->
    <button
      type="button"
      class="mv-nav mv-nav--prev"
      (click)="previous()"
      [disabled]="!state.canGoPrevious()"
      aria-label="Previous"
    >
      <app-icon name="chevronLeft" [size]="26" />
    </button>
    <button
      type="button"
      class="mv-nav mv-nav--next"
      (click)="next()"
      [disabled]="!state.canGoNext()"
      aria-label="Next"
    >
      <app-icon name="chevronRight" [size]="26" />
    </button>

    <!-- Stage -->
    <div #stage class="mv-stage" (dblclick)="onDoubleClick()">
      @if (active.type === 'image') {
        <img
          class="mv-stage__image"
          [src]="active.url"
          [alt]="active.name"
          [style.transform]="rotationStyle()"
          [class.mv-stage__image--grabbable]="state.zoom() > 100"
          [class.mv-stage__media--pending]="state.isMediaLoading()"
          draggable="false"
          (load)="onMediaLoaded()"
          (error)="onMediaError()"
        />
      } @else if (isPlayable(active)) {
        <video
          #video
          class="mv-stage__video"
          [src]="active.url"
          [class.mv-stage__media--pending]="state.isMediaLoading()"
          controls
          autoplay
          playsinline
          (loadeddata)="onMediaLoaded()"
          (error)="onMediaError()"
        >
          <track kind="captions" srclang="en" label="English" />
        </video>
      } @else {
        <div class="mv-unsupported" role="alert">
          <app-icon name="film" [size]="48" />
          <p class="mv-unsupported__text">
            This format (.{{ active.extension }}) can't be played in your browser.
          </p>
          <button type="button" class="mv-download-btn" (click)="download()">
            <app-icon name="download" [size]="16" /> Download instead
          </button>
        </div>
      }

      @if (state.isMediaLoading()) {
        <output class="mv-stage__overlay" aria-live="polite">
          <span class="mv-stage__spinner" aria-hidden="true"></span>
          <p>Loading…</p>
        </output>
      }
      @if (state.hasMediaError()) {
        <div class="mv-stage__overlay mv-stage__overlay--error" role="alert">
          <app-icon name="alert" [size]="40" />
          <p>{{ state.mediaError()?.message }}</p>
        </div>
      }
    </div>

    <!-- Video controls: playback speed -->
    @if (active.type === 'video' && isPlayable(active)) {
      <div class="mv-video-bar">
        <button
          type="button"
          class="mv-chip"
          (click)="cyclePlaybackRate()"
          ngbTooltip="Playback speed"
          aria-label="Change playback speed"
        >
          {{ playbackRate() }}×
        </button>
      </div>
    }

    <!-- Thumbnail strip -->
    @if (showThumbnails && state.filtered().length > 1) {
      <div class="mv-strip" role="tablist" aria-label="Media thumbnails">
        @for (item of state.filtered(); track trackById($index, item)) {
          <button
            type="button"
            class="mv-strip__item"
            [class.mv-strip__item--active]="$index === state.activeIndex()"
            (click)="state.goToIndex($index); emitIndexFromStrip()"
            [attr.aria-current]="$index === state.activeIndex()"
            [attr.aria-label]="item.name"
          >
            @if (thumbFor(item); as thumb) {
              <img [src]="thumb" [alt]="item.name" loading="lazy" />
            } @else {
              <app-icon [name]="item.type === 'video' ? 'film' : 'image'" [size]="18" />
            }
          </button>
        }
      </div>
    }
  </dialog>
}
